if (!customElements.get('gm-case-picker')) {
  customElements.define('gm-case-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      if (!this.data) {
        this.data = JSON.parse(this.querySelector('[data-case-data]').textContent);
        this.choices = [null, null, null];
        this.manualOverride = [false, false, false];
        this.count = this.data.mode === 'gift' ? this.clipCount() : 0;
        this.optedOut = false;
      }
      this.addEventListener('change', this.onChange.bind(this), {signal: this.controller.signal});
      this.addEventListener('click', this.onToggle.bind(this), {signal: this.controller.signal});
      document.addEventListener('gm:clip-selection', this.onClipSelection.bind(this), {signal: this.controller.signal});
      this.syncClipSelection();
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    clipWrapper() {
      const scope = this.closest('[id^="ProductInfo-"]') || this.closest('.shopify-section') || document.querySelector('#MainContent') || document;
      return scope.querySelector('.ank-wrapper');
    }

    clipSelection() {
      const wrapper = this.clipWrapper();
      if (wrapper?.gmClipSelection) return wrapper.gmClipSelection;
      const selected = wrapper?.querySelector('.ank-bundle-item.active');
      return {
        count: selected?.dataset.type === 'bundle3' ? 3 : selected?.dataset.type === 'bundle2' ? 2 : 1,
        colors: [1, 2, 3].map(slot => wrapper?.querySelector('.ank-slider-' + slot + ' .ank-slide.active')?.dataset.value || '')
      };
    }

    clipCount() {
      return this.clipSelection().count;
    }

    syncClipSelection(selection = this.clipSelection()) {
      if (this.data.mode === 'gift' && !this.optedOut) this.count = selection.count;
      selection.colors.forEach((color, slot) => {
        if (!this.manualOverride[slot] && color) this.choices[slot] = color;
      });
    }

    onClipSelection(event) {
      if (event.target !== this.clipWrapper()) return;
      this.syncClipSelection(event.detail);
      this.clearError();
      this.render();
    }

    onToggle(event) {
      if (!event.target.closest('[data-case-toggle]')) return;
      this.optedOut = !this.optedOut;
      this.count = this.optedOut ? 0 : this.clipCount();
      this.syncClipSelection();
      this.clearError();
      this.render();
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-case-count]')) {
        this.count = Number(input.value);
        this.optedOut = this.count === 0;
      } else if (input.matches('[data-case-color]')) {
        const slot = Number(input.dataset.slot);
        this.choices[slot] = input.value;
        this.manualOverride[slot] = true;
      }
      this.syncClipSelection();
      this.clearError();
      this.render();
    }

    selectedVariant(count = this.count) {
      const pack = this.data.packs[count - 1];
      if (!pack) return null;
      const color = this.choices[0];
      return pack.variants.find(v => v.available && v.color === color) || pack.variants.find(v => v.available) || null;
    }

    money(cents) {
      if (this.data.currency === 'CHF') {
        return new Intl.NumberFormat(this.data.locale === 'de' ? 'de-CH' : this.data.locale, {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(cents / 100) + '\u00a0CHF';
      }
      return new Intl.NumberFormat(this.data.locale, {style: 'currency', currency: this.data.currency}).format(cents / 100);
    }

    render() {
      const gift = this.data.mode === 'gift';
      this.querySelectorAll('[data-case-count]').forEach(input => {
        const count = Number(input.value);
        const pack = this.data.packs[count - 1];
        const available = !count || !!pack?.variants.some(v => v.available);
        input.disabled = !available || (gift && count > 0 && count !== this.clipCount());
        input.checked = count === this.count;
        const price = input.closest('label').querySelector('[data-case-price]');
        const previousPrice = input.closest('label').querySelector('[data-case-was]');
        if (price && count) {
          const variant = this.selectedVariant(count);
          previousPrice.hidden = !gift || !available;
          previousPrice.textContent = available ? this.data.text.regular + ' ' + this.money(variant.price) : '';
          price.textContent = available ? (gift ? this.data.text.now + ' ' + this.money(0) : this.money(variant.price)) : this.data.text.unavailable;
        }
      });
      const toggle = this.querySelector('[data-case-toggle]');
      toggle.setAttribute('aria-pressed', String(this.optedOut));
      this.querySelector('[data-case-fields]').hidden = this.count === 0;
      this.querySelectorAll('[data-case-slot]').forEach((slot, index) => {
        slot.hidden = index >= this.count;
        const name = slot.querySelector('[data-case-color-name]');
        name.textContent = this.choices[index] || this.data.text.choose;
        slot.querySelectorAll('[data-case-color]').forEach(input => {
          const pack = this.data.packs[this.count - 1];
          input.disabled = !pack || !pack.variants.some(v => v.available && v.color === input.value);
          input.checked = input.value === this.choices[index];
        });
      });
      this.querySelector('[data-case-gift-note]').hidden = !gift;
      this.querySelector('[data-case-paid-note]').hidden = gift;
    }

    clearError() {
      const error = this.querySelector('[data-case-error]');
      error.hidden = true;
      error.textContent = '';
      this.querySelectorAll('[aria-invalid]').forEach(el => el.removeAttribute('aria-invalid'));
    }

    getCartItems(quantity) {
      this.clearError();
      this.syncClipSelection();
      if (!this.count) return [];
      if (this.data.mode === 'gift' && this.count !== this.clipCount()) {
        this.count = this.clipCount();
        this.render();
      }
      for (let slot = 0; slot < this.count; slot++) {
        if (!this.choices[slot]) {
          const field = this.querySelectorAll('[data-case-slot]')[slot];
          field.setAttribute('aria-invalid', 'true');
          this.showError(this.data.text.required.replace('__NUMBER__', String(slot + 1)));
          field.querySelector('input:not(:disabled)')?.focus();
          throw new Error('case-selection-incomplete');
        }
      }
      const pack = this.data.packs[this.count - 1];
      if (this.choices.slice(0, this.count).some(color => !pack?.variants.some(variant => variant.available && variant.color === color))) {
        this.showError(this.data.text.unavailable);
        throw new Error('case-unavailable');
      }
      const variant = this.selectedVariant();
      if (!variant || variant.color !== this.choices[0]) {
        this.showError(this.data.text.unavailable);
        throw new Error('case-unavailable');
      }
      const properties = {};
      this.choices.slice(0, this.count).forEach((color, index) => {
        properties[this.count === 1 ? 'Farbe' : 'Farbe ' + (index + 1)] = color;
      });
      return [{id: variant.id, quantity, properties}];
    }

    showError(message) {
      const error = this.querySelector('[data-case-error]');
      error.textContent = message;
      error.hidden = false;
    }
  });
}

